import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  ArrowBadge,
  HeartIcon,
  NextIcon2,
  PrevIcon2,
  VerifiedBadge,
} from "./Icon";
import * as anchor from '@project-serum/anchor';
import idl from '../assets/IDL/idl.json';

const PROGRAM_ID = 'HfotedVpaC45b87hS5QZGpUxn87tg9uHRZeFxDGnD5NP';
const RPC_URL = `https://mainnet.helius-rpc.com/?api-key=${process.env.REACT_APP_API_KEY}`;
const PRESALE_SEED = "sol244";

const TokenItem = ({ item, i }) => {
  const {
    img,
    authorImg,
    name,
    totalAmount,
    raisedAmount,
    projectLink,
    isOctonet // New flag to identify Octonet
  } = item;

  const [currentRaised, setCurrentRaised] = useState(raisedAmount);

  // Only setup Solana connection if it's Octonet
  const connection = isOctonet ? new anchor.web3.Connection(RPC_URL, 'confirmed') : null;

  const getProgram = useCallback(() => {
    if (!isOctonet) return null;

    try {
      const provider = new anchor.AnchorProvider(
        connection,
        {
          publicKey: anchor.web3.PublicKey.default,
          signTransaction: () => Promise.reject(),
          signAllTransactions: () => Promise.reject(),
        },
        { commitment: 'confirmed' }
      );
      return new anchor.Program(idl, PROGRAM_ID, provider);
    } catch (error) {
      console.error('Error creating program:', error);
      return null;
    }
  }, [connection, isOctonet]);

  const fetchPresaleData = useCallback(async () => {
    if (!isOctonet) return;

    const program = getProgram();
    if (!program) return;

    try {
      const [presaleAccountPda] = await anchor.web3.PublicKey.findProgramAddress(
        [Buffer.from(PRESALE_SEED)],
        program.programId
      );

      const presaleAccountInfo = await program.account.presaleAccount.fetch(presaleAccountPda);
      const raised = presaleAccountInfo.totalContributed.toNumber() / anchor.web3.LAMPORTS_PER_SOL;
      setCurrentRaised(raised);
    } catch (error) {
      console.error('Error fetching presale data:', error);
    }
  }, [getProgram, isOctonet]);

  useEffect(() => {
    if (isOctonet) {
      fetchPresaleData();
      const intervalId = setInterval(fetchPresaleData, 5000);
      return () => clearInterval(intervalId);
    }
  }, [fetchPresaleData, isOctonet]);

  const calculateProgress = () => {
    return (currentRaised / totalAmount) * 100;
  };

  return (
    <div className="col-xl-4 col-md-6">
      <div className="launchpad-item">
        <div className="img">
          <div className="kyc-button-tags">
            <span className="kyc-button-tag">KYC</span>
            <span className="kyc-button-tag">Safu</span>
            <span className="kyc-button-tag">Tech</span>
          </div>
          <img src={img} alt={name} />
        </div>
        <div className="content">
          <div className="author">
            <div className="author-container">
              <img
                src={authorImg}
                alt={`${name} Mini`}
                className="author-image"
              />
            </div>
            <div className="author-name">
              <h6>
                {name} <span className="text-base"></span>
              </h6>
            </div>
          </div>
          <div className="price-area poppins">
            <div className="d-flex gap-2 align-items-center mt-3 mb-3">
              <div className="__progress" style={{ flex: 1 }}>
                <div
                  className="progress-bar"
                  style={{
                    width: `${calculateProgress()}%`,
                  }}
                />
              </div>
              <span className="text-nowrap">
                {currentRaised.toFixed(2)}/{totalAmount} SOL
              </span>
            </div>
          </div>
          <div className="btn-area poppins d-flex flex-wrap gap-3 justify-content-between">
            <button className="secondary-btn" type="button">
              <span className="text-gradient">
                {isOctonet ? "Filled" : "Filled"}
              </span>
            </button>
            <Link to={projectLink} className="cmn-btn">
              Explore <ArrowBadge />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

// Updated data array
const data = [
  {
    img: "/img/solchat77.png",
    authorImg: "/img/sc11.png",
    name: "Solchat",
    totalAmount: 500,
    raisedAmount: 500,
    projectLink: "/solchat-presale",
    isOctonet: false
  },
  {
    img: "/img/octoccc.png",
    authorImg: "/img/octo555.png",
    name: "OctonetAI",
    totalAmount: 2000,
    raisedAmount: 0,
    projectLink: "/octonetai-presale",
    isOctonet: true // Flag to identify Octonet
  }
];

// Main Launchpad component remains the same
const Launchpad = ({ title, pagination }) => {
  return (
    <section className="launchpad-list">
      <div className="container">
        {title && <h5 className="launchpad--title">{title}</h5>}
        <div className="row g-4">
          {data?.map((item, i) => (
            <TokenItem key={i} item={item} i={i} />
          ))}
        </div>
        {pagination && (
          <div className="mt-4 mt-lg-5">
            <ul className="pagination justify-content-center">
              <li className="page-item disabled">
                <Link className="page-link" to="#" tabIndex="-1" aria-disabled="true">
                  <PrevIcon2 />
                </Link>
              </li>
              <li className="page-item active">
                <Link className="page-link" to="#">1</Link>
              </li>
              <li className="page-item">
                <Link className="page-link" to="#">2</Link>
              </li>
              <li className="page-item">
                <Link className="page-link" to="#">3</Link>
              </li>
              <li className="page-item">
                <Link className="page-link" to="#"><NextIcon2 /></Link>
              </li>
            </ul>
          </div>
        )}
      </div>
    </section>
  );
};

export default Launchpad;